// Sista Kyerewaa — Interactive Checklist Library
// Each checklist is a standalone interactive resource for African families in Germany

export const CHECKLISTS = [
  {
    slug: 'first-month',
    title: 'Your First Month in Germany',
    subtitle: 'The essential steps for African families arriving in Germany',
    description: 'From Anmeldung to Krankenversicherung — everything you need to do in your first 30 days, in the right order.',
    icon: '🏠',
    tags: ['Integration', 'Practical Tips'],
    affiliate: null,
    sections: [
      {
        title: 'Week 1 — Register your address (Anmeldung)',
        items: [
          { id: 'a1', text: 'Download and complete the Anmeldeformular (registration form)', tip: 'Find it on your city council website — search "[your city] Anmeldeformular PDF"' },
          { id: 'a2', text: 'Get your Wohnungsgeberbestätigung (landlord confirmation letter)', tip: 'Your landlord must provide this. It\'s their legal obligation.' },
          { id: 'a3', text: 'Book an appointment at the Bürgeramt', tip: 'Search "[your city] Bürgeramt Termin online" — book ahead, walk-ins can take hours' },
          { id: 'a4', text: 'Bring all documents in a clear folder: passport, Anmeldeformular, landlord letter', tip: 'Arrive 10 minutes early. Staff are helpful.' },
          { id: 'a5', text: 'Receive and file your Anmeldebestätigung (registration certificate)', tip: 'Keep this document safe — you need it for almost everything else on this list' },
        ]
      },
      {
        title: 'Week 2 — Health insurance (Krankenversicherung)',
        items: [
          { id: 'b1', text: 'Decide between public (GKV) or private (PKV) insurance', tip: 'Most African families start with GKV (public). Your employer usually registers you automatically if employed.' },
          { id: 'b2', text: 'If self-arranging: contact a public insurer (AOK, TK, Barmer, DAK)', tip: 'TK (Techniker Krankenkasse) has good English support' },
          { id: 'b3', text: 'Register your children under your insurance (Familienversicherung — free)', tip: 'Children are fully covered under a parent\'s GKV at no extra cost' },
          { id: 'b4', text: 'Receive and activate your Krankenkarte (health insurance card)', tip: 'Always carry this. Doctors will ask for it at every appointment.' },
          { id: 'b5', text: 'Find a Hausarzt (family doctor / GP) near you', tip: 'Search "Hausarzt [your city] new patients accepting". Register even before you need one.' },
        ]
      },
      {
        title: 'Week 3 — Banking & finances',
        items: [
          { id: 'c1', text: 'Open a German bank account (Girokonto)', tip: 'N26 and DKB are good online options — fully English, no branch visit needed' },
          { id: 'c2', text: 'Set up your online banking', tip: 'Download the app. Set up your PIN. Confirm your identity if required.' },
          { id: 'c3', text: 'Register your bank account with your employer for salary payments', tip: 'Your IBAN is on your bank card or in your online banking app' },
          { id: 'c4', text: 'Wait for your Steuer-Identifikationsnummer (tax ID) by post', tip: 'It arrives automatically 2–4 weeks after Anmeldung. Do NOT call to request it early.' },
          { id: 'c5', text: 'Share your tax ID with your employer once it arrives', tip: 'Required for payroll. Your HR team will ask for it.' },
        ]
      },
      {
        title: 'Week 4 — Daily life basics',
        items: [
          { id: 'd1', text: 'Register for a German language course (Integrationskurs)', tip: 'Usually subsidised by the government — ask your local VHS (Volkshochschule) or BAMF office' },
          { id: 'd2', text: 'Get a German SIM card or transfer your number', tip: 'Aldi Talk, Lebara, and O2 are affordable. You need your Anmeldebestätigung to register.' },
          { id: 'd3', text: 'Set up liability insurance (Haftpflichtversicherung)', tip: 'Strongly recommended — covers accidental damage to others\' property. From ~€5/month.' },
          { id: 'd4', text: 'Locate your nearest African food shop', tip: 'Search "Afrikanischer Lebensmittelladen [your city]" — there are more than you think!' },
          { id: 'd5', text: 'Connect with the local African/Ghanaian community', tip: 'Facebook groups and church communities are the best starting points in most German cities' },
        ]
      }
    ]
  },

  {
    slug: 'pre-arrival',
    title: 'Pre-Arrival: Before You Leave for Germany',
    subtitle: 'Prepare properly before you travel — save weeks of stress on arrival',
    description: 'Documents to gather, accounts to open, and things to organise before you leave home. Being prepared makes the first week in Germany much smoother.',
    icon: '✈️',
    tags: ['Integration', 'Practical Tips'],
    affiliate: null,
    sections: [
      {
        title: 'Documents to prepare',
        items: [
          { id: 'pa1', text: 'Passport (valid for at least 6 months beyond your planned stay)', tip: 'Renew now if needed — German embassies require this' },
          { id: 'pa2', text: 'Certified copies of all educational certificates', tip: 'Get them officially translated into German before you leave — much cheaper in Ghana than in Germany' },
          { id: 'pa3', text: 'Birth certificate (and children\'s if applicable)', tip: 'Get an official translated copy. Required for school enrollment and some official registrations.' },
          { id: 'pa4', text: 'Marriage certificate (if married)', tip: 'Translated copy needed for family-related registrations' },
          { id: 'pa5', text: 'Medical records and vaccination history', tip: 'German doctors will ask about your history. Your children\'s Impfpass (vaccination booklet) is especially important.' },
          { id: 'pa6', text: 'Police clearance certificate from your home country', tip: 'Some employers and visa categories require this. Get it certified.' },
        ]
      },
      {
        title: 'Financial preparation',
        items: [
          { id: 'pb1', text: 'Open a blocked account (Sperrkonto) if required for your visa', tip: 'Required for student and job-seeker visa. Expatrio and Coracle offer this service.' },
          { id: 'pb2', text: 'Set up international transfer capability on your home bank account', tip: 'You will need to send/receive money internationally at first' },
          { id: 'pb3', text: 'Bring sufficient cash for your first 2–4 weeks', tip: 'Germany is still very cash-heavy. Have at least €500 in cash on arrival.' },
          { id: 'pb4', text: 'Research Lemfi or Wise for sending money between Germany and home', tip: 'Much cheaper than traditional bank wires. Set up your account before you travel.' },
        ]
      },
      {
        title: 'Housing & logistics',
        items: [
          { id: 'pc1', text: 'Confirm your accommodation address for Anmeldung on arrival', tip: 'You need a fixed address to register. Short-term lets and student halls both count.' },
          { id: 'pc2', text: 'Download offline maps of your destination city', tip: 'Google Maps, Maps.me — download before you fly in case of no data' },
          { id: 'pc3', text: 'Save emergency numbers: 110 (police), 112 (emergency), 116 117 (medical on-call)', tip: 'Store these in your phone before you land' },
          { id: 'pc4', text: 'Join diaspora Facebook groups for your destination city', tip: 'Search "Ghanaians in [city]" or "Africans in [city]" — invaluable for practical tips' },
        ]
      }
    ]
  },

  {
    slug: 'school-enrollment',
    title: 'Enrolling Your Child at School in Germany',
    subtitle: 'A step-by-step checklist for Ghanaian and African parents',
    description: 'School enrollment in Germany has specific requirements and timings. This checklist helps you prepare the right documents and ask the right questions.',
    icon: '🎒',
    tags: ['Life in Germany', 'Integration'],
    affiliate: null,
    sections: [
      {
        title: 'Before you go to the school',
        items: [
          { id: 's1', text: 'Find your child\'s assigned school (Grundschule / Gymnasium)', tip: 'In Germany, school assignment is based on your address catchment area. Ask your Bürgeramt which school your address belongs to.' },
          { id: 's2', text: 'Get your child\'s most recent school report translated into German', tip: 'Official translation (by a certified translator / beeidigter Übersetzer) may be required' },
          { id: 's3', text: 'Prepare your child\'s birth certificate (translated German copy)', tip: 'Required for enrollment. Have both original and translated version.' },
          { id: 's4', text: 'Gather vaccination records (Impfpass)', tip: 'German schools check vaccination status, especially for measles. Missing vaccinations may need to be updated.' },
          { id: 's5', text: 'Have your Anmeldebestätigung ready', tip: 'Proof of address registration — schools require this' },
          { id: 's6', text: 'Book an enrollment appointment (Anmeldetermin) with the school', tip: 'Call the school directly and ask for an Anmeldetermin. Bring a German-speaking friend if possible.' },
        ]
      },
      {
        title: 'At the enrollment meeting',
        items: [
          { id: 's7', text: 'Bring all documents in a clear folder', tip: 'Originals AND copies of everything' },
          { id: 's8', text: 'Ask about your child\'s German language assessment (Sprachstandstest)', tip: 'Most schools assess German level and may offer preparation classes (Vorbereitungsklasse / DaZ)' },
          { id: 's9', text: 'Ask about the class teacher (Klassenlehrer) and how to contact them', tip: 'Building a direct relationship with the class teacher is very important in German schools' },
          { id: 's10', text: 'Ask for a school supply list (Schulmaterialliste)', tip: 'German schools often require specific supplies. Ask at enrollment to prepare early.' },
          { id: 's11', text: 'Enquire about school lunch (Mittagessen) and after-school care (Hort/OGS)', tip: 'These may be available for working parents but require separate registration' },
        ]
      },
      {
        title: 'After enrollment',
        items: [
          { id: 's12', text: 'Set up a communication method with the school (app, email, paper)', tip: 'Many German schools use apps like SchulComm or Eltern-App. Ask which one they use.' },
          { id: 's13', text: 'Prepare your child culturally — explain that German school routines differ', tip: 'More independent learning, parent-teacher evenings (Elternabend), different grading (1=best, 6=worst)' },
          { id: 's14', text: 'Find a homework support group or Hausaufgabenhilfe in your area', tip: 'Many community centres and churches offer free homework help for children with migrant backgrounds' },
        ]
      }
    ]
  },

  {
    slug: 'job-search',
    title: 'Finding a Job in Germany',
    subtitle: 'A practical checklist for African professionals navigating the German job market',
    description: 'The German job application process has very specific expectations around CV format, cover letters, and professional etiquette. This checklist guides you through it step by step.',
    icon: '💼',
    tags: ['Life in Germany', 'Integration', 'Practical Tips'],
    affiliate: null,
    sections: [
      {
        title: 'Prepare your documents',
        items: [
          { id: 'j1', text: 'Create a German-format CV (Lebenslauf)', tip: 'German CVs include a professional photo, date of birth, and full address. Use a clean, chronological format — no "skills infographics".' },
          { id: 'j2', text: 'Write a tailored cover letter (Anschreiben) for each application', tip: 'German cover letters are formal and specific. Address the hiring manager by name if possible. One page maximum.' },
          { id: 'j3', text: 'Get your qualifications recognised (Anerkennung)', tip: 'Check anabin.kmk.org to see if your foreign degree is recognised. NARIC and Anabin are the key databases.' },
          { id: 'j4', text: 'Prepare German translations of your certificates if needed', tip: 'Some employers require certified translations (beglaubigte Übersetzung)' },
          { id: 'j5', text: 'Gather references from previous employers (Arbeitszeugnisse)', tip: 'German employers expect formal written references. Request them from your previous employers.' },
        ]
      },
      {
        title: 'Where to search',
        items: [
          { id: 'j6', text: 'Register on the Bundesagentur für Arbeit (Federal Employment Agency) portal', tip: 'jobboerse.arbeitsagentur.de — the official German job portal' },
          { id: 'j7', text: 'Set up profiles on XING and LinkedIn', tip: 'XING is the German equivalent of LinkedIn and is widely used for professional networking' },
          { id: 'j8', text: 'Search sector-specific job portals', tip: 'StepStone, Indeed.de, and Glassdoor.de are popular. Healthcare workers: KFN, Medi-Jobs.' },
          { id: 'j9', text: 'Reach out to African professional networks in Germany', tip: 'Search "African professionals Germany" on LinkedIn and Facebook. Community connections often lead to referrals.' },
          { id: 'j10', text: 'Register with a Zeitarbeit (temporary work) agency if open to temp work', tip: 'A great way to get experience, references, and permanent offers in Germany' },
        ]
      },
      {
        title: 'During the application process',
        items: [
          { id: 'j11', text: 'Learn standard German interview etiquette', tip: 'Arrive on time (5 minutes early). Use formal "Sie" unless invited to use "du". Firm handshake. Bring printed copies of your application.' },
          { id: 'j12', text: 'Prepare answers to common German interview questions', tip: 'Germans value: reliability, specific examples of past work, why you chose this specific company.' },
          { id: 'j13', text: 'Understand the probation period (Probezeit)', tip: 'Typically 3–6 months. Both parties can terminate easily during this period. Perform consistently from day one.' },
          { id: 'j14', text: 'Negotiate your salary with confidence', tip: 'Research Gehalt.de or StepStone salary data for your role and region. Germans respect informed negotiation.' },
        ]
      }
    ]
  },

  {
    slug: 'citizenship',
    title: 'Preparing for German Citizenship',
    subtitle: 'Requirements, timeline, and documents for naturalisation in Germany',
    description: 'German citizenship (Einbürgerung) is achievable with the right preparation. This checklist covers the general requirements — always confirm current rules with your local Ausländerbehörde.',
    icon: '🇩🇪',
    tags: ['Integration', 'Life in Germany', 'Practical Tips'],
    affiliate: null,
    sections: [
      {
        title: 'Check your eligibility',
        items: [
          { id: 'ci1', text: 'Confirm your required years of legal residence (usually 5 years, 3 with special merits)', tip: 'Recent reforms allow naturalisation after 5 years of legal residence, reduced to 3 for special contributions' },
          { id: 'ci2', text: 'Check your German language level meets B1 minimum requirement', tip: 'Official certificate required (Goethe-Institut, telc, or ÖSD). Higher levels strengthen your application.' },
          { id: 'ci3', text: 'Confirm you can support yourself financially without Bürgergeld / social benefits', tip: 'You must not have received social benefits in the last 2 years (with some exceptions)' },
          { id: 'ci4', text: 'Check if your home country allows dual citizenship', tip: 'Germany now allows dual citizenship in more cases. Confirm your specific situation with the Ausländerbehörde.' },
          { id: 'ci5', text: 'Confirm you have no criminal record in Germany', tip: 'Minor traffic violations are usually overlooked. More serious offences may disqualify you.' },
        ]
      },
      {
        title: 'Prepare your documents',
        items: [
          { id: 'ci6', text: 'Valid passport (your home country passport)', tip: 'Must be valid. Renew before starting the process if it expires soon.' },
          { id: 'ci7', text: 'Current Niederlassungserlaubnis or EU residence permit', tip: 'Permanent residence (unbefristete Aufenthaltserlaubnis) usually required' },
          { id: 'ci8', text: 'German language certificate (minimum B1)', tip: 'Official certificate only — self-assessment not accepted' },
          { id: 'ci9', text: 'Proof of employment or self-employment (Einkommensnachweis)', tip: 'Last 3 months of pay slips or tax returns for self-employed' },
          { id: 'ci10', text: 'Proof of accommodation (Mietvertrag / rental agreement)', tip: 'Current address must match your Anmeldebestätigung' },
          { id: 'ci11', text: 'Birth certificate (translated German copy)', tip: 'Get an officially certified translation' },
          { id: 'ci12', text: 'Complete the Einbürgerungstest (naturalisation test)', tip: '33 questions about German society, history, and law. Study materials free at bamf.de.' },
        ]
      },
      {
        title: 'The application process',
        items: [
          { id: 'ci13', text: 'Book an appointment at your local Einbürgerungsbehörde (naturalisation office)', tip: 'Different from the Ausländerbehörde. Search "Einbürgerungsbehörde [your city]"' },
          { id: 'ci14', text: 'Submit your application with all documents', tip: 'Processing typically takes 12–24 months. Be patient.' },
          { id: 'ci15', text: 'Attend the Einbürgerungsfeier (citizenship ceremony) when invited', tip: 'A formal ceremony where you receive your certificate and take the oath of loyalty' },
        ]
      }
    ]
  }
];

export const getChecklistBySlug = (slug) =>
  CHECKLISTS.find(c => c.slug === slug) || null;

export default CHECKLISTS;
